import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon';
import { useModal } from '../context/ModalContext';
import '../styles/PromptHub.css';

const PROMPT_SECTIONS = [
  { key: 'meetings', label: 'Meetings' },
  { key: 'ideas', label: 'Ideas' },
  { key: 'content', label: 'Content' },
  { key: 'brands', label: 'Brands' },
];

const PromptHub = ({ user }) => {
  const modal = useModal();
  const [activeSection, setActiveSection] = useState(() => {
    return localStorage.getItem('prompt_hub_active_section') || 'prompts';
  });

  // Prompts state
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Avatars state
  const [avatars, setAvatars] = useState([]);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [avatarsLoading, setAvatarsLoading] = useState(false);

  // Default Brand Voice state
  const [defaultVoice, setDefaultVoice] = useState(null);
  const [defaultVoiceLoading, setDefaultVoiceLoading] = useState(false);

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // For adding new traits/guidelines
  const [newTrait, setNewTrait] = useState({ name: '', description: '' });
  const [newGuideline, setNewGuideline] = useState('');

  useEffect(() => {
    if (activeSection === 'prompts') {
      fetchPrompts();
    } else if (activeSection === 'brand-voice') {
      fetchAvatars();
    } else if (activeSection === 'default-voice') {
      fetchDefaultVoice();
    }
  }, [activeSection]);

  useEffect(() => {
    localStorage.setItem('prompt_hub_active_section', activeSection);
  }, [activeSection]);

  const fetchPrompts = async () => {
    try {
      setPromptsLoading(true);
      const response = await axios.get('/api/prompt-hub/prompts', { withCredentials: true });
      if (response.data.success) {
        setPrompts(response.data.prompts);
      }
    } catch (error) {
      console.error('Error fetching prompts:', error);
      setError('Failed to load prompts');
    } finally {
      setPromptsLoading(false);
    }
  };

  const fetchAvatars = async () => {
    try {
      setAvatarsLoading(true);
      const response = await axios.get('/api/prompt-hub/avatars', { withCredentials: true });
      if (response.data.success) {
        setAvatars(response.data.avatars);
      }
    } catch (error) {
      console.error('Error fetching avatars:', error);
      setError('Failed to load avatars');
    } finally {
      setAvatarsLoading(false);
    }
  };

  const fetchDefaultVoice = async () => {
    try {
      setDefaultVoiceLoading(true);
      const response = await axios.get('/api/prompt-hub/default-brand-voice', { withCredentials: true });
      if (response.data.success) {
        setDefaultVoice(response.data.default_voice);
        if (response.data.default_voice) {
          setEditData(response.data.default_voice);
        }
      }
    } catch (error) {
      console.error('Error fetching default brand voice:', error);
      setError('Failed to load default brand voice');
    } finally {
      setDefaultVoiceLoading(false);
    }
  };

  const getFilteredPrompts = () => {
    let filtered = prompts;

    if (searchQuery.trim()) {
      filtered = filtered.filter(p =>
        (p.prompt_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.prompt_key.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.description || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  };

  const getGroupedPrompts = () => {
    const filtered = getFilteredPrompts();
    const groups = PROMPT_SECTIONS.map(section => ({
      ...section,
      prompts: filtered.filter(p => p.category === section.key),
    }));

    const knownKeys = PROMPT_SECTIONS.map(s => s.key);
    const other = filtered.filter(p => !knownKeys.includes(p.category));
    if (other.length > 0) {
      groups.push({ key: 'other', label: 'Other', prompts: other });
    }

    return groups;
  };


  const startEditingAvatar = () => {
    setEditData({ ...selectedAvatar });
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditData({});
    setNewTrait({ name: '', description: '' });
    setNewGuideline('');
    setError(null);
  };


  const saveAvatar = async () => {
    try {
      setSaving(true);
      setError(null);

      await axios.put(`/api/prompt-hub/avatars/${editData.avatar_id}`, editData, { withCredentials: true });
      setSuccess('Brand voice updated successfully');

      await fetchAvatars();
      setSelectedAvatar(editData);
      setIsEditing(false);
      setEditData({});
      setNewTrait({ name: '', description: '' });
      setNewGuideline('');

      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving avatar:', error);
      setError(error.response?.data?.error || 'Failed to save brand voice');
    } finally {
      setSaving(false);
    }
  };

  const addTrait = () => {
    if (!newTrait.name.trim() || !newTrait.description.trim()) return;

    setEditData(prev => ({
      ...prev,
      traits: [...(prev.traits || []), { ...newTrait }]
    }));
    setNewTrait({ name: '', description: '' });
  };

  const removeTrait = (index) => {
    setEditData(prev => ({
      ...prev,
      traits: prev.traits.filter((_, i) => i !== index)
    }));
  };

  const addGuideline = () => {
    if (!newGuideline.trim()) return;

    setEditData(prev => ({
      ...prev,
      guidelines: [...(prev.guidelines || []), newGuideline.trim()]
    }));
    setNewGuideline('');
  };

  const removeGuideline = (index) => {
    setEditData(prev => ({
      ...prev,
      guidelines: prev.guidelines.filter((_, i) => i !== index)
    }));
  };

  const saveDefaultVoice = async () => {
    try {
      setSaving(true);
      setError(null);

      await axios.put('/api/prompt-hub/default-brand-voice', editData, { withCredentials: true });
      setSuccess('Default brand voice updated successfully');

      await fetchDefaultVoice();
      setIsEditing(false);

      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving default brand voice:', error);
      setError(error.response?.data?.error || 'Failed to save default brand voice');
    } finally {
      setSaving(false);
    }
  };

  const addExcerpt = () => {
    setEditData(prev => ({
      ...prev,
      excerpts: [...(prev.excerpts || []), '']
    }));
  };

  const updateExcerpt = (index, value) => {
    setEditData(prev => ({
      ...prev,
      excerpts: prev.excerpts.map((excerpt, i) => i === index ? value : excerpt)
    }));
  };

  const removeExcerpt = (index) => {
    setEditData(prev => ({
      ...prev,
      excerpts: prev.excerpts.filter((_, i) => i !== index)
    }));
  };

  const addSample = () => {
    setEditData(prev => ({
      ...prev,
      samples: [...(prev.samples || []), '']
    }));
  };

  const updateSample = (index, value) => {
    setEditData(prev => ({
      ...prev,
      samples: prev.samples.map((sample, i) => i === index ? value : sample)
    }));
  };

  const removeSample = (index) => {
    setEditData(prev => ({
      ...prev,
      samples: prev.samples.filter((_, i) => i !== index)
    }));
  };



  const deleteAvatar = async (avatarId) => {
    const result = await modal.confirm('Archive this brand voice? It will no longer appear in the Brand Voice Designer.', 'Archive Brand Voice');
    if (!result) return;

    try {
      await axios.delete(`/api/prompt-hub/avatars/${avatarId}`, { withCredentials: true });
      setSuccess('Brand voice archived successfully');
      setSelectedAvatar(null);
      setIsEditing(false);
      await fetchAvatars();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error archiving brand voice:', error);
      setError(error.response?.data?.error || 'Failed to archive brand voice');
    }
  };

  const renderPromptsPanel = () => {
    const groupedPrompts = getGroupedPrompts();

    return (
      <div className="ph-split-layout">
        {/* Left Side - Prompts List */}
        <div className="ph-sidebar">
          <div className="ph-sidebar-header">
            <h3>Prompts</h3>
          </div>

          <div className="ph-search">
            <Icon name="search" size="0.875rem" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {promptsLoading ? (
            <div className="ph-sidebar-loading">Loading...</div>
          ) : (
            <div className="ph-item-list">
              {groupedPrompts.map(group => group.prompts.length > 0 && (
                <div key={group.key} className="ph-group">
                  <div className="ph-group-header">{group.label}</div>
                  {group.prompts.map(prompt => (
                    <div
                      key={prompt._id}
                      className={`ph-list-item ${selectedPrompt?._id === prompt._id ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedPrompt(prompt);
                        setError(null);
                        setSuccess(null);
                      }}
                    >
                      <div className="ph-list-item-content">
                        <div className="ph-list-item-title">{prompt.prompt_name}</div>
                        <div className="ph-list-item-subtitle">{prompt.prompt_key}</div>
                      </div>
                      <span className={`ph-list-item-badge ${prompt.status}`}>
                        {prompt.status}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Side - Prompt Details (Read-Only) */}
        <div className="ph-content">
          {error && (
            <div className="ph-alert ph-alert-error">
              {error}
              <button onClick={() => setError(null)}>&times;</button>
            </div>
          )}
          {success && (
            <div className="ph-alert ph-alert-success">
              {success}
              <button onClick={() => setSuccess(null)}>&times;</button>
            </div>
          )}

          {selectedPrompt ? (
            <>
              <div className="ph-content-header">
                <div>
                  <h2>{selectedPrompt.prompt_name}</h2>
                  <p>{selectedPrompt.description || 'No description'}</p>
                  <p className="ph-meta" style={{ marginTop: '0.5rem' }}>Last updated: {new Date(selectedPrompt.updated_at).toLocaleDateString()}</p>
                </div>
              </div>

              <div className="ph-content-body">
                <div className="ph-detail-section">
                  <h4>Prompt Key</h4>
                  <p className="ph-mono">{selectedPrompt.prompt_key}</p>
                </div>

                <div className="ph-detail-section">
                  <h4>Category</h4>
                  <p>{selectedPrompt.category}</p>
                </div>

                <div className="ph-detail-section">
                  <h4>Prompt Text</h4>
                  <pre className="ph-prompt-text">{selectedPrompt.prompt_text}</pre>
                </div>
              </div>
            </>
          ) : (
            <div className="ph-empty-state">
              <Icon name="doc-text" size="3rem" />
              <h3>Select a prompt</h3>
              <p>Choose a prompt from the list to view</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAvatarsPanel = () => {
    const activeAvatars = avatars.filter(a => a.status === 'active');
    const displayData = isEditing ? editData : selectedAvatar;

    return (
      <div className="ph-split-layout">
        {/* Left Side - Avatars List */}
        <div className="ph-sidebar">
          <div className="ph-sidebar-header">
            <h3>Brand Voices</h3>
          </div>

          {avatarsLoading ? (
            <div className="ph-sidebar-loading">Loading...</div>
          ) : (
            <div className="ph-item-list">
              {activeAvatars.map(avatar => (
                <div
                  key={avatar._id}
                  className={`ph-list-item ${selectedAvatar?._id === avatar._id ? 'selected' : ''}`}
                  onClick={() => {
                    if (!isEditing) {
                      setSelectedAvatar(avatar);
                      setError(null);
                      setSuccess(null);
                    }
                  }}
                >
                  <div className="ph-avatar-badge">{avatar.avatar_id}</div>
                  <div className="ph-list-item-content">
                    <div className="ph-list-item-title">{avatar.avatar_name}</div>
                    <div className="ph-list-item-subtitle">Type {avatar.avatar_type}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Side - Avatar Details/Edit */}
        <div className="ph-content">
          {error && (
            <div className="ph-alert ph-alert-error">
              {error}
              <button onClick={() => setError(null)}>&times;</button>
            </div>
          )}
          {success && (
            <div className="ph-alert ph-alert-success">
              {success}
              <button onClick={() => setSuccess(null)}>&times;</button>
            </div>
          )}

          {displayData ? (
            <>
              <div className="ph-content-header">
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                    <div className="ph-avatar-id-large">{displayData.avatar_id}</div>
                    <div>
                      {isEditing ? (
                        <>
                          <input
                            type="text"
                            className="ph-edit-title"
                            value={editData.avatar_name}
                            onChange={(e) => setEditData({ ...editData, avatar_name: e.target.value })}
                            placeholder="Avatar Name"
                          />
                          <input
                            type="text"
                            className="ph-edit-input-small"
                            value={editData.avatar_type || ''}
                            onChange={(e) => setEditData({ ...editData, avatar_type: e.target.value })}
                            placeholder="Type (e.g., 1, 2, 3)"
                            style={{ marginTop: '0.5rem' }}
                          />
                        </>
                      ) : (
                        <>
                          <h2>{displayData.avatar_name}</h2>
                          <p>Type {displayData.avatar_type}</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="ph-content-actions">
                  {isEditing ? (
                    <>
                      <button className="ph-btn-secondary" onClick={cancelEditing} disabled={saving}>
                        Cancel
                      </button>
                      <button className="ph-btn-primary" onClick={saveAvatar} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="ph-btn-secondary" onClick={startEditingAvatar}>
                        <Icon name="edit" size="1rem" />
                        Edit
                      </button>
                      <button className="ph-btn-danger" onClick={() => deleteAvatar(displayData.avatar_id)}>
                        <Icon name="cancel" size="1rem" />
                        Archive
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="ph-content-body">
                <div className="ph-detail-section">
                  <h4>Keywords</h4>
                  {isEditing ? (
                    <input
                      type="text"
                      className="ph-edit-input"
                      value={(editData.keywords || []).join(', ')}
                      onChange={(e) => setEditData({ ...editData, keywords: e.target.value.split(',').map(k => k.trim()) })}
                      placeholder="Enter keywords separated by commas"
                    />
                  ) : (
                    <div className="ph-tags">
                      {displayData.keywords?.map((keyword, idx) => (
                        <span key={idx} className="ph-tag">{keyword}</span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="ph-detail-section">
                  <h4>Explainer</h4>
                  {isEditing ? (
                    <textarea
                      className="ph-edit-textarea"
                      value={editData.explainer || ''}
                      onChange={(e) => setEditData({ ...editData, explainer: e.target.value })}
                      placeholder="Short description of this avatar's voice characteristics"
                      rows={4}
                    />
                  ) : (
                    <p>{displayData.explainer}</p>
                  )}
                </div>

                <div className="ph-detail-section">
                  <h4>Introduction / Persona</h4>
                  {isEditing ? (
                    <textarea
                      className="ph-edit-textarea"
                      value={editData.introduction || ''}
                      onChange={(e) => setEditData({ ...editData, introduction: e.target.value })}
                      placeholder="Introduction text for the brand"
                      rows={3}
                    />
                  ) : (
                    <p>{displayData.introduction}</p>
                  )}
                </div>

                <div className="ph-detail-section">
                  <h4>Traits</h4>
                  {isEditing ? (
                    <div className="ph-traits-edit">
                      {(editData.traits || []).map((trait, idx) => (
                        <div key={idx} className="ph-trait-edit-item">
                          <div className="ph-trait-edit-fields">
                            <input
                              type="text"
                              className="ph-edit-input"
                              value={trait.name}
                              onChange={(e) => {
                                const newTraits = [...editData.traits];
                                newTraits[idx].name = e.target.value;
                                setEditData({ ...editData, traits: newTraits });
                              }}
                              placeholder="Trait Name"
                            />
                            <textarea
                              className="ph-edit-textarea"
                              value={trait.description}
                              onChange={(e) => {
                                const newTraits = [...editData.traits];
                                newTraits[idx].description = e.target.value;
                                setEditData({ ...editData, traits: newTraits });
                              }}
                              placeholder="Trait Description"
                              rows={2}
                            />
                          </div>
                          <button
                            className="ph-remove-btn"
                            onClick={() => removeTrait(idx)}
                            title="Remove trait"
                          >
                            <Icon name="cancel" size="1rem" />
                          </button>
                        </div>
                      ))}
                      <div className="ph-add-trait">
                        <input
                          type="text"
                          className="ph-edit-input"
                          value={newTrait.name}
                          onChange={(e) => setNewTrait({ ...newTrait, name: e.target.value })}
                          placeholder="New Trait Name"
                        />
                        <textarea
                          className="ph-edit-textarea"
                          value={newTrait.description}
                          onChange={(e) => setNewTrait({ ...newTrait, description: e.target.value })}
                          placeholder="Trait Description"
                          rows={2}
                        />
                        <button className="ph-btn-secondary" onClick={addTrait}>
                          <Icon name="plus" size="1rem" />
                          Add Trait
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="ph-traits-list">
                      {displayData.traits?.map((trait, idx) => (
                        <div key={idx} className="ph-trait-item">
                          <strong>{trait.name}</strong>
                          <p>{trait.description}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="ph-detail-section">
                  <h4>Guidelines</h4>
                  {isEditing ? (
                    <div className="ph-guidelines-edit">
                      {(editData.guidelines || []).map((guideline, idx) => (
                        <div key={idx} className="ph-guideline-edit-item">
                          <textarea
                            className="ph-edit-textarea"
                            value={guideline}
                            onChange={(e) => {
                              const newGuidelines = [...editData.guidelines];
                              newGuidelines[idx] = e.target.value;
                              setEditData({ ...editData, guidelines: newGuidelines });
                            }}
                            rows={2}
                          />
                          <button
                            className="ph-remove-btn"
                            onClick={() => removeGuideline(idx)}
                            title="Remove guideline"
                          >
                            <Icon name="cancel" size="1rem" />
                          </button>
                        </div>
                      ))}
                      <div className="ph-add-guideline">
                        <textarea
                          className="ph-edit-textarea"
                          value={newGuideline}
                          onChange={(e) => setNewGuideline(e.target.value)}
                          placeholder="New Guideline"
                          rows={2}
                        />
                        <button className="ph-btn-secondary" onClick={addGuideline}>
                          <Icon name="plus" size="1rem" />
                          Add Guideline
                        </button>
                      </div>
                    </div>
                  ) : (
                    <ul className="ph-guidelines-list">
                      {displayData.guidelines?.map((guideline, idx) => (
                        <li key={idx}>{guideline}</li>
                      ))}
                    </ul>
                  )}
                </div>

                {!isEditing && (
                  <div className="ph-detail-section">
                    <p className="ph-meta">Last updated: {new Date(displayData.updated_at).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="ph-empty-state">
              <Icon name="user" size="3rem" />
              <h3>Select a brand voice</h3>
              <p>Choose a brand voice from the list to view or edit</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDefaultVoicePanel = () => {
    return (
      <div className="ph-content ph-default-voice-container">
        {error && (
          <div className="ph-alert ph-alert-error">
            {error}
            <button onClick={() => setError(null)}>&times;</button>
          </div>
        )}
        {success && (
          <div className="ph-alert ph-alert-success">
            {success}
            <button onClick={() => setSuccess(null)}>&times;</button>
          </div>
        )}

        {defaultVoiceLoading ? (
          <div className="ph-sidebar-loading">Loading...</div>
        ) : !defaultVoice ? (
          <div className="ph-empty">
            <h3>Default Brand Voice Not Configured</h3>
            <p>The default brand voice has not been set up yet.</p>
          </div>
        ) : (
          <>
            <div className="ph-default-voice-header">
              <div>
                <h2>{isEditing ? (
                  <input
                    type="text"
                    className="ph-edit-title"
                    value={editData.name || ''}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    placeholder="Voice Name"
                  />
                ) : defaultVoice.name || 'Default Brand Voice'}</h2>
                <p>
                  System-wide fallback voice for brands without custom voice
                </p>
              </div>
              <div className="ph-content-actions">
                {isEditing ? (
                  <>
                    <button className="ph-btn-secondary" onClick={cancelEditing} disabled={saving}>
                      Cancel
                    </button>
                    <button className="ph-btn-primary" onClick={saveDefaultVoice} disabled={saving}>
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </>
                ) : (
                  <button className="ph-btn-primary" onClick={() => setIsEditing(true)}>
                    Edit
                  </button>
                )}
              </div>
            </div>

            <div className="ph-content-body">
              {/* Description */}
              <div className="ph-default-voice-section">
                <h4 className="ph-default-voice-section-title">Voice Description</h4>
                {isEditing ? (
                  <textarea
                    className="ph-edit-textarea"
                    value={editData.description || ''}
                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                    placeholder="Describe the brand voice characteristics..."
                    rows={10}
                  />
                ) : (
                  <p className="ph-default-voice-description">{defaultVoice.description}</p>
                )}
              </div>

              {/* Excerpts */}
              <div className="ph-default-voice-section">
                {isEditing ? (
                  <div className="ph-default-voice-section-title-with-button">
                    <h4 style={{ margin: 0, border: 'none', padding: 0 }}>Voice Excerpts</h4>
                    <button className="ph-btn-secondary" onClick={addExcerpt}>
                      <Icon name="plus" size="1rem" />
                      Add Excerpt
                    </button>
                  </div>
                ) : (
                  <h4 className="ph-default-voice-section-title">Voice Excerpts</h4>
                )}
                {isEditing ? (
                  <div className="ph-excerpts-edit">
                    {(editData.excerpts || []).map((excerpt, index) => (
                      <div key={index} className="ph-excerpt-edit-item">
                        <input
                          type="text"
                          className="ph-edit-input"
                          value={excerpt}
                          onChange={(e) => updateExcerpt(index, e.target.value)}
                          placeholder={`Excerpt ${index + 1}`}
                        />
                        <button
                          className="ph-remove-btn"
                          onClick={() => removeExcerpt(index)}
                          title="Remove excerpt"
                        >
                          <Icon name="cancel" size="1rem" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ph-excerpts-list">
                    {(defaultVoice.excerpts || []).map((excerpt, index) => (
                      <div key={index} className="ph-excerpt-item">
                        <p>{excerpt}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Samples */}
              <div className="ph-default-voice-section">
                {isEditing ? (
                  <div className="ph-default-voice-section-title-with-button">
                    <h4 style={{ margin: 0, border: 'none', padding: 0 }}>Voice Samples</h4>
                    <button className="ph-btn-secondary" onClick={addSample}>
                      <Icon name="plus" size="1rem" />
                      Add Sample
                    </button>
                  </div>
                ) : (
                  <h4 className="ph-default-voice-section-title">Voice Samples</h4>
                )}
                {isEditing ? (
                  <div className="ph-samples-edit">
                    {(editData.samples || []).map((sample, index) => (
                      <div key={index} className="ph-sample-edit-item">
                        <div className="ph-sample-edit-header">
                          <strong>Sample {index + 1}</strong>
                          <button
                            className="ph-remove-btn"
                            onClick={() => removeSample(index)}
                            title="Remove sample"
                          >
                            <Icon name="cancel" size="1rem" />
                          </button>
                        </div>
                        <textarea
                          className="ph-edit-textarea"
                          value={sample}
                          onChange={(e) => updateSample(index, e.target.value)}
                          placeholder={`Sample ${index + 1}`}
                          rows={8}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ph-samples-list">
                    {(defaultVoice.samples || []).map((sample, index) => (
                      <div key={index} className="ph-sample-item">
                        <strong>Sample {index + 1}</strong>
                        <p>{sample}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="super-admin-internal-layout">
      <nav className="internal-nav">
        <div className="internal-nav-items">
          <button
            className={`internal-nav-btn ${activeSection === 'prompts' ? 'active' : ''}`}
            onClick={() => setActiveSection('prompts')}
          >
            Prompts
          </button>
          <button
            className={`internal-nav-btn ${activeSection === 'brand-voice' ? 'active' : ''}`}
            onClick={() => setActiveSection('brand-voice')}
          >
            Brand Voice Designer
          </button>
          <button
            className={`internal-nav-btn ${activeSection === 'default-voice' ? 'active' : ''}`}
            onClick={() => setActiveSection('default-voice')}
          >
            Default Brand Voice
          </button>
        </div>
      </nav>

      <div className="internal-content">
        {activeSection === 'prompts' ? renderPromptsPanel() :
         activeSection === 'brand-voice' ? renderAvatarsPanel() :
         renderDefaultVoicePanel()}
      </div>
    </div>
  );
};

export default PromptHub;
